// ── Issue types ──────────────────────────────────────────────
export type Priority = 'High' | 'Medium' | 'Low';
export type Status = 'Open' | 'In Progress' | 'Closed';
export type UserRole = 'admin' | 'developer' | 'tester' | 'super_admin';
export type ReviewStatus = 'not_required' | 'ready_for_review' | 'approved' | 'rework_required';

export interface Profile {
  user_id: string;
  email: string;
  full_name: string | null;
  company_id: string;
  role: UserRole;
  created_at: string;
  updated_at: string;
}

export interface Company {
  id: string;
  name: string;
  invite_code: string;
  created_by: string | null;
  created_at: string;
}

export interface UserMembership {
  user_id: string;
  company_id: string;
  role: UserRole;
  joined_at: string;
  companies?: Company;
}

export type ProjectStatus = 'active' | 'archived';
export type ProjectVisibility = 'public' | 'private';

export interface Project {
  id: string;
  name: string;
  description: string;
  link: string | null;
  company_id: string;
  created_by: string | null;
  status: ProjectStatus;
  visibility: ProjectVisibility;
  created_at: string;
  updated_at: string;
}

export interface Issue {
  id: string;
  title: string;
  description: string;
  priority: Priority;
  status: Status;
  user_id: string;
  company_id: string;
  created_by: string;
  assigned_developer_id: string | null;
  assigned_tester_id: string | null;
  reviewed_by: string | null;
  review_status: ReviewStatus;
  project_id: string;
  images: string[];
  close_reason: string | null;
  close_proof_image: string | null;
  closed_at: string | null;
  closed_by: string | null;
  closer_name?: string | null;
  closer_email?: string | null;
  created_at: string;
  updated_at: string;
}

export interface IssueFormData {
  title: string;
  description: string;
  priority: Priority;
  status: Status;
  images: File[];
}

export interface IssueComment {
  id: string;
  issue_id: string;
  user_id: string;
  content: string;
  created_at: string;
  profiles?: { full_name: string | null; email: string };
}

export type NotificationType = 'issue_created' | 'issue_assigned' | 'status_changed' | 'review_requested';

export interface AppNotification {
  id: string;
  user_id: string;
  company_id: string;
  issue_id: string | null;
  type: NotificationType;
  title: string;
  body: string;
  read: boolean;
  created_at: string;
}

// ── Task types ───────────────────────────────────────────────
export type TaskStatus = 'Pending' | 'In Progress' | 'Completed';
export type TaskCategory =
  | 'Development'
  | 'Design'
  | 'Testing'
  | 'Meeting'
  | 'Research'
  | 'General'
  | 'Other';
export type Recurrence = 'none' | 'daily' | 'weekly';

export interface Task {
  id: string;
  user_id: string;
  company_id: string;
  created_by: string;
  assigned_developer_id: string | null;
  assigned_tester_id: string | null;
  reviewed_by: string | null;
  review_status: ReviewStatus;
  title: string;
  description: string;
  priority: Priority;
  status: TaskStatus;
  category: TaskCategory;
  progress: number;           // 0–100
  deadline: string | null;    // ISO timestamptz
  task_date: string;          // YYYY-MM-DD
  recurrence: Recurrence;
  completed_at: string | null;
  carried_over: boolean;
  original_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaskNote {
  id: string;
  task_id: string;
  user_id: string;
  content: string;
  created_at: string;
}

// ── Shared helpers ───────────────────────────────────────────
export const CATEGORIES: TaskCategory[] = [
  'Development', 'Design', 'Testing', 'Meeting', 'Research', 'General', 'Other',
];

export const CATEGORY_COLORS: Record<TaskCategory, string> = {
  Development: '#3b82f6',
  Design:      '#a855f7',
  Testing:     '#f59e0b',
  Meeting:     '#1C9B82',
  Research:    '#6366f1',
  General:     '#6b7280',
  Other:       '#ec4899',
};
